import React, { useCallback, useMemo, useRef, useState } from 'react';
import { fetchNavHTML } from '~/libs/server/nav';
import { fetchUser } from '~/libs/server/users';
import { GetServerSideProps } from 'next';
import IncomingMessageWithCookies from '~/types/IncomingMessageWithCookies';

import { WebsiteLpProps } from '~/components/WebsiteLp/WebsiteLp';
import { fetchSEORelatedLinks } from '~/libs/server/seo';

import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import type { WRelatedLinkView } from '@zola/svc-web-api-ts-client';

import { faqItems } from '~/components/LP/SeatingChart/faqItems';
import { gridItems } from '~/components/LP/SeatingChart/gridItems';
import AppDownloadModal from '~/components/LP/SeatingChart/AppDownloadModal/AppDownloadModal';
import {
  PrimaryNavIdentifier,
  SecondaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import { useIsPostAuthNav3MobileOrDesktop } from '~/hooks/useIsPostAuthNav3Active';
import { getIsGuest } from '~/libs/universal/utils/getIsGuest';

import { AppDownloadBannerMobile } from '@zola/zola-ui/src/components/AppDownloadBannerMobile/AppDownloadBannerMobile';
import GridTwoByTwo from '@zola/zola-ui/src/components/PreAuthV3/GridTwoByTwo';
import Header from '~/components/LP/SeatingChart/Header';
import Nav from '~/components/Nav';
import NextBackgroundForegroundImages from '~/components/PreauthV3/NextBackgroundForegroundImages';
import Masthead from '@zola/zola-ui/src/components/PreAuthV3/Masthead';
import SupportAccordion from '@zola/zola-ui/src/components/PreAuthV3/SupportAccordion';
import { useResponsiveDesign } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignContext';
import { ResponsiveDesignProvider } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignProvider';

import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import { PageWrapper } from '~/components/LP/v3/PageWrapper.styles';
import {
  StyledHTitle3,
  CtaSection,
  CtaText,
  CtaButton,
  Highlight,
} from '~/components/LP/SeatingChart/SeatingChartLp.styles';
import Footer from '~/components/ui/Footer/Footer';
import { SecondaryNavType } from '~/components/Nav/types';

const HeroDesktop = '/static-preauth/images/SeatingChartLp/hero/hero_desktop.webp';

type SeatingChartLPProps = {
  userAgent: string;
  navHTML: string | null;
  userCtx: UserContext | null;
  adviceLinks: WRelatedLinkView[];
};

const SeatingChartLP: React.FC<SeatingChartLPProps> = (props): JSX.Element => {
  const { navHTML, userCtx, adviceLinks } = props;
  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);
  const { isDesktop } = useResponsiveDesign();
  const modalTriggerRef = useRef<HTMLButtonElement>(null);
  const [appDownloadModalOpen, setAppDownloadModalOpen] = useState(false);

  const appStoreUrl = useMemo(() => {
    const campaignSuffix = userCtx?.is_guest ? 'preauth' : 'postauth';
    return `https://apps.apple.com/app/apple-store/id852691916?pt=31209800&ct=zola_web_seating_chart_lp_${campaignSuffix}&mt=8`;
  }, [userCtx]);

  const handleAppDownloadClick = useCallback(
    (e?: React.MouseEvent<Element>) => {
      if (isDesktop) {
        e?.preventDefault();
        e?.stopPropagation();
        setAppDownloadModalOpen(true);
      }
    },
    [isDesktop]
  );

  return (
    <>
      <Header />
      {getIsGuest(userCtx) ? <AppDownloadBannerMobile campaignName="zola_web_lp_preauth" /> : <></>}
      <Nav
        navHTML={navHTML}
        showPromo
        user={userCtx}
        disablePrimaryNavCollapse
        disableSecondary={userCtx?.is_guest || userCtx?.wedding_account?.seating_chart_v1}
        secondaryNavType={SecondaryNavType.GUEST_LIST}
        activeSecondaryLink={SecondaryNavIdentifier.SEATING}
        {...(isPostAuthNav3Enabled && { activePrimaryLink: PrimaryNavIdentifier.SEATING })}
      />
      <PageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <section>
          <Masthead
            btnHref={appStoreUrl}
            btnOnClick={handleAppDownloadClick}
            btnText="Try it in our iOS app"
            buttonPropOverrides={{
              primary: {
                textTransform: false,
              },
            }}
            isPhotoOnRight
            altMedia={
              <NextBackgroundForegroundImages
                bgColor={`var(--background-screen, ${COLORS3.WHITE_100})`}
                foregroundImg={{
                  src: HeroDesktop,
                  alt: 'Create your seating chart in minutes',
                  objectFit: 'cover',
                }}
                priority
              />
            }
            title={
              <h1>
                <span>Wedding seating chart</span>: seat all your guests in minutes
              </h1>
            }
            valueProps={[
              'Seat unlimited guests, right from your guest list',
              'Customize your floor plans for multiple wedding events ',
              'Easily download, print, and share your chart',
            ]}
          />
        </section>
        <GridTwoByTwo
          gridItems={gridItems(appStoreUrl, handleAppDownloadClick)}
          title={
            <StyledHTitle3 presentation="h2">
              Seamless seating chart maker: seat, share, celebrate!
            </StyledHTitle3>
          }
        />
        <CtaSection>
          <CtaText>
            Ready to <Highlight>jump in?</Highlight>
          </CtaText>
          <CtaButton
            component="a"
            href={appStoreUrl}
            onClick={handleAppDownloadClick}
            size="large"
            textTransform={false}
          >
            Download the iOS app
          </CtaButton>
        </CtaSection>
        <section>
          <SupportAccordion advice={{ links: adviceLinks }} faqs={faqItems} collapsed />
        </section>
        <Footer userCtx={userCtx} />
        {appDownloadModalOpen && (
          <AppDownloadModal
            source={userCtx?.is_guest ? 'SEATING_CHART_PREAUTH' : 'SEATING_CHART_POSTAUTH'}
            triggerRef={modalTriggerRef}
            onClose={() => setAppDownloadModalOpen(false)}
          />
        )}
      </PageWrapper>
    </>
  );
};

const SeatingChartLPContainer: React.FC<SeatingChartLPProps> = props => {
  const { userAgent } = props;
  return (
    <ResponsiveDesignProvider userAgent={userAgent}>
      <SeatingChartLP {...props} />
    </ResponsiveDesignProvider>
  );
};

export const getServerSideProps: GetServerSideProps<WebsiteLpProps> = async ({
  req,
}: {
  req: IncomingMessageWithCookies;
}) => {
  const userAgent = req.headers['user-agent'];

  const [navHTML, userCtx, adviceLinks] = await Promise.all([
    fetchNavHTML(req),
    fetchUser(req),
    fetchSEORelatedLinks({
      topicSlug: 'wedding-planning-101',
      subtopicSlug: 'wedding-planning-tools',
    }),
  ]);

  return {
    props: {
      userAgent,
      navHTML,
      userCtx,
      adviceLinks,
    },
  };
};

export default SeatingChartLPContainer;
